import { create } from 'zustand';
import type { TimelineLayer, Keyframe, Tween } from '../types';
import { sortTimelineLayers } from '../util/timeline';

interface TimelineState {
  // State
  layers: TimelineLayer[];
  keyframes: Record<string, Keyframe[]>; // layerId -> keyframes
  tweens: Record<string, Tween[]>; // layerId -> tweens
  totalFrames: number;
  visibleFrameStart: number;
  visibleFrameEnd: number;
  selectedKeyframe: { layerId: string; frame: number } | null;
  selectedTween: { layerId: string; startFrame: number } | null;

  // Layer actions
  setLayers: (layers: TimelineLayer[]) => void;
  addLayer: (layer: TimelineLayer) => void;
  updateLayer: (layerId: string, updates: Partial<TimelineLayer>) => void;
  deleteLayer: (layerId: string) => void;
  reorderLayers: (layerIds: string[]) => void;
  toggleLayerVisibility: (layerId: string) => void;
  toggleLayerLock: (layerId: string) => void;

  // Keyframe actions
  setKeyframes: (layerId: string, keyframes: Keyframe[]) => void;
  addKeyframe: (keyframe: Keyframe) => void;
  updateKeyframe: (
    layerId: string,
    frame: number,
    updates: Partial<Keyframe>
  ) => void;
  deleteKeyframe: (layerId: string, frame: number) => void;
  deleteAllKeyframes: (layerId: string) => void;
  getKeyframeAt: (layerId: string, frame: number) => Keyframe | undefined;

  // Tween actions
  setTweens: (layerId: string, tweens: Tween[]) => void;
  addTween: (tween: Tween) => void;
  updateTween: (
    tweenId: string,
    updates: Partial<Tween>
  ) => void;
  deleteTween: (layerId: string, startFrame: number) => void;
  deleteAllTweens: (layerId: string) => void;

  // View actions
  setTotalFrames: (frames: number) => void;
  setVisibleRange: (start: number, end: number) => void;
  scrollTimeline: (delta: number) => void;
  setSelectedKeyframe: (selection: { layerId: string; frame: number } | null) => void;
  setSelectedTween: (selection: { layerId: string; startFrame: number } | null) => void;
  clearTimelineSelection: () => void;

  // Bulk operations
  clearAll: () => void;
  resetTimeline: () => void;
  loadTimelineData: (data: {
    layers: TimelineLayer[];
    keyframes: Record<string, Keyframe[]>;
    tweens: Record<string, Tween[]>;
  }) => void;
}

export const useTimelineStore = create<TimelineState>((set, get) => ({
  // Initial state
  layers: [],
  keyframes: {},
  tweens: {},
  totalFrames: 240, // 10 seconds at 24fps
  visibleFrameStart: 0,
  visibleFrameEnd: 100,
  selectedKeyframe: null,
  selectedTween: null,

  // Layer actions
  setLayers: (layers) => set({ layers }),

  addLayer: (layer) =>
    set((state) => ({ layers: [...state.layers, layer] })),

  updateLayer: (layerId, updates) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId ? { ...l, ...updates } : l
      ),
    })),

  deleteLayer: (layerId) =>
    set((state) => {
      const nextKeyframes = { ...state.keyframes };
      delete nextKeyframes[layerId];
      const nextTweens = { ...state.tweens };
      delete nextTweens[layerId];
      const selectedKeyframe =
        state.selectedKeyframe?.layerId === layerId ? null : state.selectedKeyframe;
      const selectedTween =
        state.selectedTween?.layerId === layerId ? null : state.selectedTween;
      return {
        layers: state.layers.filter((l) => l.id !== layerId),
        keyframes: nextKeyframes,
        tweens: nextTweens,
        selectedKeyframe,
        selectedTween,
      };
    }),

  reorderLayers: (layerIds) =>
    set((state) => {
      const layerMap = new Map(state.layers.map((l) => [l.id, l]));
      const reordered = layerIds
        .map((id, index) => {
          const layer = layerMap.get(id);
          return layer ? { ...layer, order: index } : null;
        })
        .filter((l): l is TimelineLayer => l !== null);
      return { layers: reordered };
    }),

  toggleLayerVisibility: (layerId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId ? { ...l, visible: !l.visible } : l
      ),
    })),

  toggleLayerLock: (layerId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId ? { ...l, locked: !l.locked } : l
      ),
    })),

  // Keyframe actions
  setKeyframes: (layerId, keyframes) =>
    set((state) => ({
      keyframes: { ...state.keyframes, [layerId]: keyframes },
    })),

  addKeyframe: (keyframe) =>
    set((state) => {
      const existing = state.keyframes[keyframe.layerId] ?? [];
      // Replace if keyframe at same frame exists
      const filtered = existing.filter((k) => k.frame !== keyframe.frame);
      return {
        keyframes: {
          ...state.keyframes,
          [keyframe.layerId]: [...filtered, keyframe].sort(
            (a, b) => a.frame - b.frame
          ),
        },
      };
    }),

  updateKeyframe: (layerId, frame, updates) =>
    set((state) => ({
      keyframes: {
        ...state.keyframes,
        [layerId]: (state.keyframes[layerId] ?? []).map((k) =>
          k.frame === frame ? { ...k, ...updates } : k
        ),
      },
    })),

  deleteKeyframe: (layerId, frame) =>
    set((state) => ({
      keyframes: {
        ...state.keyframes,
        [layerId]: (state.keyframes[layerId] ?? []).filter(
          (k) => k.frame !== frame
        ),
      },
    })),

  deleteAllKeyframes: (layerId) =>
    set((state) => {
      const nextKeyframes = { ...state.keyframes };
      delete nextKeyframes[layerId];
      return { keyframes: nextKeyframes };
    }),

  getKeyframeAt: (layerId, frame) => {
    const keyframes = get().keyframes[layerId] ?? [];
    return keyframes.find((k) => k.frame === frame);
  },

  // Tween actions
  setTweens: (layerId, tweens) =>
    set((state) => ({
      tweens: { ...state.tweens, [layerId]: tweens },
    })),

  addTween: (tween) =>
    set((state) => {
      const existing = state.tweens[tween.layerId] ?? [];
      return {
        tweens: {
          ...state.tweens,
          [tween.layerId]: [...existing, tween].sort(
            (a, b) => a.startFrame - b.startFrame
          ),
        },
      };
    }),

  updateTween: (tweenId, updates) =>
    set((state) => {
      const entry = Object.entries(state.tweens).find(([, tweens]) =>
        tweens.some((t) => t.id === tweenId)
      );
      if (!entry) return {};

      const [layerId, existingTweens] = entry;
      const existing = existingTweens.find((t) => t.id === tweenId);
      if (!existing) return {};

      const nextTweensForLayer = existingTweens.map((t) =>
        t.id === tweenId ? { ...t, ...updates } : t
      );
      return {
        tweens: {
          ...state.tweens,
          [layerId]: nextTweensForLayer,
        },
      };
    }),

  deleteTween: (layerId, startFrame) =>
    set((state) => ({
      tweens: {
        ...state.tweens,
        [layerId]: (state.tweens[layerId] ?? []).filter(
          (t) => t.startFrame !== startFrame
        ),
      },
    })),

  deleteAllTweens: (layerId) =>
    set((state) => {
      const nextTweens = { ...state.tweens };
      delete nextTweens[layerId];
      return { tweens: nextTweens };
    }),

  // View actions
  setTotalFrames: (frames) => set({ totalFrames: Math.max(1, frames) }),

  setVisibleRange: (start, end) =>
    set({
      visibleFrameStart: Math.max(0, start),
      visibleFrameEnd: Math.max(start + 1, end),
    }),

  scrollTimeline: (delta) =>
    set((state) => {
      const range = state.visibleFrameEnd - state.visibleFrameStart;
      const newStart = Math.max(0, state.visibleFrameStart + delta);
      const newEnd = newStart + range;
      return {
        visibleFrameStart: newStart,
        visibleFrameEnd: newEnd,
      };
    }),

  setSelectedKeyframe: (selection) => set({ selectedKeyframe: selection }),
  setSelectedTween: (selection) => set({ selectedTween: selection }),
  clearTimelineSelection: () => set({ selectedKeyframe: null, selectedTween: null }),

  // Bulk operations
  clearAll: () => set({ layers: [], keyframes: {}, tweens: {}, selectedKeyframe: null, selectedTween: null }),

  resetTimeline: () =>
    set({
      layers: [],
      keyframes: {},
      tweens: {},
      totalFrames: 240,
      visibleFrameStart: 0,
      visibleFrameEnd: 100,
      selectedKeyframe: null,
      selectedTween: null,
    }),

  loadTimelineData: (data) =>
    set({
      layers: sortTimelineLayers(data.layers).map((layer, index) => ({ ...layer, order: index })),
      keyframes: data.keyframes,
      tweens: data.tweens,
      selectedKeyframe: null,
      selectedTween: null,
    }),
}));
